import type { TFunction } from "i18next";
import type {
  QuickSearchSelectionEnrichmentConfig,
  QuickSearchSelectionMetadata,
} from "@/types/QuickSearch";
import { formatDateValue } from "@/components/lms/lms.utils";
import { normalizeQuickSearchSelection } from "@/helper/quickSearchSelection";

export const LMS_START_VERTRAG_SELECTION_ENRICHMENT: QuickSearchSelectionEnrichmentConfig =
  {
    responseField: "start_vertrag_lms",
    booleanField: "is_active",
  };

export const normalizeLmsStartVertragSelection = (
  responseData: unknown
): QuickSearchSelectionMetadata | null => {
  if (
    typeof responseData !== "object" ||
    responseData === null ||
    !(LMS_START_VERTRAG_SELECTION_ENRICHMENT.responseField in responseData)
  ) {
    return null;
  }
  return normalizeQuickSearchSelection(
    responseData,
    LMS_START_VERTRAG_SELECTION_ENRICHMENT
  );
};

export const getLmsSelectionBlockedMessage = (
  selection: QuickSearchSelectionMetadata,
  t: TFunction
) => {
  if (selection.reason === "lms_starts_in_future") {
    return t("lms_quicksearch_starts_in_future", {
      date: formatDateValue(String(selection.context.start_date ?? "")),
    });
  }

  if (selection.reason === "lms_not_booked") {
    return t("lms_quicksearch_not_booked");
  }

  if (selection.reason === "lms_contract_ended") {
    return t("lms_quicksearch_contract_ended", {
      date: formatDateValue(String(selection.context.end_date ?? "")),
    });
  }

  if (selection.reason === "lms_contract_end_invalid") {
    return t("lms_quicksearch_contract_end_invalid");
  }

  if (selection.reason === "lms_mandatory_data_missing") {
    return t("lms_quicksearch_mandatory_data_missing");
  }

  return null;
};

export const localizeLmsContractEligibilityError = (
  error: any,
  t: TFunction
) => {
  const errorData = error?.response?.data ?? error;
  const courseErrors = Array.isArray(errorData?.course)
    ? errorData.course
    : [errorData?.course];

  if (courseErrors.includes("course_not_enrolment_eligible")) {
    return t("course_enrolment_requires_scorm");
  }

  const failures = errorData?.lms_contract?.failures;
  if (!Array.isArray(failures) || failures.length === 0) {
    return error;
  }

  return failures.map((failure: any) =>
    getLmsSelectionBlockedMessage(
      {
        selectable: false,
        reason: failure?.reason ?? "lms_not_booked",
        context: failure?.context ?? { start_date: null },
      },
      t
    )
  );
};
